<script setup>
  import { computed, watchEffect } from 'vue';
  import { useBrowseCartStore } from '../../stores/BrowseCartStore';
  import { useOfferStore } from '../../stores/OfferStore';
  import { useI18n } from 'vue-i18n';
  import { showPriceWithCurrency } from '../../utils/format_offer'

  const { t } = useI18n();
  const offerStore = useOfferStore();
  const cartStore = useBrowseCartStore();

  const loading = computed(() => {
    return offerStore.loading;
  });

  const offers = computed(() => {
    return offerStore.offers;
  });

  const allComputedPrices = () => {
    const { billing_currency } = cartStore.cart.financial;

    const computedPrice = {
      currency: billing_currency
    };

    const convertedTotalsByCurrency = [];
    const totalTaxes = [];
    let feesAmount = 0;
    let totalWithTaxes = 0;
    if (offers) {
      offers.value.forEach((offer) => {
        if (offer.only_offer_deleted || !offer.original_offer) return;

        const { price } = offer;
        if (!price) return;
        if (Object.keys(price).length === 0) return;

        const existingconvertedTotalsByCurrency = convertedTotalsByCurrency.find((item) => item.currency === price.currency);
        if (existingconvertedTotalsByCurrency) {
          existingconvertedTotalsByCurrency.totalWithoutTaxes += price.price_without_taxes * offer.offer.quantity;
          existingconvertedTotalsByCurrency.convertedTotalWithoutTaxes += price.converted_price_without_taxes * offer.offer.quantity;
        } else {
          convertedTotalsByCurrency.push({
            currency: price.currency,
            totalWithoutTaxes: price.price_without_taxes * offer.offer.quantity,
            convertedTotalWithoutTaxes: price.converted_price_without_taxes * offer.offer.quantity
          });
        }

        feesAmount += price.fees * offer.offer.quantity;

        if (Object.keys(price.total_taxes).length !== 0) {
          price.total_taxes.forEach((tax) => {
            const existingTax = totalTaxes.find((item) => item.name === tax.name);
            if (existingTax) {
              existingTax.amount += tax.amount * offer.offer.quantity;
            } else {
              totalTaxes.push({
                name: tax.name,
                amount: tax.amount * offer.offer.quantity,
                rate: tax.rate
              });
            }
          });
        }

        totalWithTaxes += price.total_amount * offer.offer.quantity;
      });
    }
    computedPrice.convertedTotalsByCurrency = convertedTotalsByCurrency;

    const subTotalWithoutTaxes = convertedTotalsByCurrency.reduce((acc, item) => acc + item.convertedTotalWithoutTaxes, 0);
    computedPrice.subTotalWithoutTaxes = subTotalWithoutTaxes;
    computedPrice.totalWithoutTaxes = subTotalWithoutTaxes;

    if(feesAmount > 0) {
      computedPrice.service_fees = {
        amount: feesAmount,
        rate: cartStore.cart.financial.service_fee
      }
      computedPrice.totalWithoutTaxes += feesAmount;
    }

    computedPrice.totalTaxes = totalTaxes;
    computedPrice.totalWithTaxes = totalWithTaxes;

    cartStore.setAllComputedPrices(computedPrice);
  };

  watchEffect(() => {
    allComputedPrices();
  });

  const priceDetailByCurrency = (item) => {
    const currency = (billingCurrency.value === item.currency) ? item.currency.toUpperCase() : showPriceWithCurrency(item.currency, item.totalWithoutTaxes)
    return t('cart.carts.total_by_currency', { currency: currency });
  };

  const showOtherCurrencies = computed(() => {
    const currencies = convertedTotalsByCurrency.value;

    if (currencies.length === 1 && currencies[0].currency === billingCurrency.value) {
      return false;
    }
    return true;
  });

  const allPrices = computed(() => {
    return cartStore.allComputedPrices;
  });

  const convertedTotalsByCurrency = computed(() => {
    return allPrices.value.convertedTotalsByCurrency || [];
  });

  const billingCurrency = computed(() => {
    return allPrices.value.currency;
  });

  const serviceFees = computed(() => {
    return allPrices.value.service_fees;
  });

const subTotalWithoutTaxes = computed(() => {
  return allPrices.value.subTotalWithoutTaxes;
});

const totalWithoutTaxes = computed(() => {
  return allPrices.value.totalWithoutTaxes;
});

const totalTaxes = computed(() => {
  return allPrices.value.totalTaxes || [];
});
</script>

<template>
  <div class="cart-price-details cart-subtotals" v-if="!loading">
    <div class="cart-price-detail cart-subtotals-items" v-for="item in convertedTotalsByCurrency" v-if="showOtherCurrencies">
      <span class="cart-price-detail-label">{{ priceDetailByCurrency(item) }}</span>
      <span class="cart-price-detail-content cart-price-amount">{{ showPriceWithCurrency(billingCurrency, item.convertedTotalWithoutTaxes) }}</span>
    </div>
    <template v-if="serviceFees">
      <div class="cart-price-detail cart-subtotals-items">
        <span class="cart-price-detail-label">{{ t('cart.carts.sub_total_without_taxes') }}</span>
        <span class="cart-price-detail-content cart-price-amount">{{ showPriceWithCurrency(billingCurrency, subTotalWithoutTaxes) }}</span>
      </div>
      <div class="cart-price-detail cart-subtotals-items">
        <span class="cart-price-detail-label">{{ t('cart.carts.service_fees', { rate: serviceFees.rate}) }}</span>
        <span class="cart-price-detail-content cart-price-amount">{{ showPriceWithCurrency(billingCurrency, serviceFees.amount) }}</span>
      </div>
    </template>
    <div class="cart-price-detail cart-subtotals-items">
      <span class="cart-price-detail-label">{{ t('cart.carts.total_without_taxes') }}</span>
      <span class="cart-price-detail-content cart-price-amount">{{ showPriceWithCurrency(billingCurrency, totalWithoutTaxes) }}</span>
    </div>
    <div class="cart-price-detail cart-subtotals-items" v-for="tax in totalTaxes" :key="tax.name">
      <span class="cart-price-detail-label">{{ t(`cart.carts.tax_rates.${tax.name}`) }} ({{ tax.rate }}%)</span>
      <span class="cart-price-detail-content cart-price-amount">{{ showPriceWithCurrency(billingCurrency, tax.amount) }}</span>
    </div>
  </div>
</template>
